import {
  Cookie,
  Laugh,
  Moon,
  Palette,
  Smile,
  Sparkles,
  Sunrise,
  Target,
  Users,
  Waves,
  Wind,
  Zap,
  type LucideIcon,
} from 'lucide-react'

/**
 * One source of truth for the `EffectTag` vocabulary.
 *
 * The set is closed and fixed by the backend enum, so label/icon/colour live on
 * the client — no round-trip, and every surface (product editor, hover cards,
 * POS, storefront) renders an effect identically.
 *
 * Colours follow the existing convention in this codebase: a `-600/-700` tone
 * for light mode with a `-400` counterpart for dark. Each is visually distinct
 * from its neighbours so a row of chips is scannable at a glance.
 */

export interface EffectMeta {
  /** Human label — sentence case, as shown in the pickers. */
  label: string
  icon: LucideIcon
  /** Icon/text colour, light + dark. */
  color: string
  /** Tinted background + border, for chip surfaces. */
  chip: string
}

export const EFFECT_VOCAB: Record<string, EffectMeta> = {
  RELAXED: {
    label: 'Relaxed',
    icon: Waves,
    color: 'text-teal-700 dark:text-teal-400',
    chip: 'bg-teal-500/10 border-teal-500/20',
  },
  SLEEPY: {
    label: 'Sleepy',
    icon: Moon,
    color: 'text-indigo-700 dark:text-indigo-400',
    chip: 'bg-indigo-500/10 border-indigo-500/20',
  },
  CALM: {
    label: 'Calm',
    icon: Wind,
    color: 'text-sky-700 dark:text-sky-400',
    chip: 'bg-sky-500/10 border-sky-500/20',
  },
  EUPHORIC: {
    label: 'Euphoric',
    icon: Sparkles,
    color: 'text-fuchsia-700 dark:text-fuchsia-400',
    chip: 'bg-fuchsia-500/10 border-fuchsia-500/20',
  },
  HAPPY: {
    label: 'Happy',
    icon: Smile,
    color: 'text-amber-700 dark:text-amber-400',
    chip: 'bg-amber-500/10 border-amber-500/20',
  },
  UPLIFTED: {
    label: 'Uplifted',
    icon: Sunrise,
    color: 'text-orange-700 dark:text-orange-400',
    chip: 'bg-orange-500/10 border-orange-500/20',
  },
  FOCUSED: {
    label: 'Focused',
    icon: Target,
    color: 'text-blue-700 dark:text-blue-400',
    chip: 'bg-blue-500/10 border-blue-500/20',
  },
  CREATIVE: {
    label: 'Creative',
    icon: Palette,
    color: 'text-violet-700 dark:text-violet-400',
    chip: 'bg-violet-500/10 border-violet-500/20',
  },
  ENERGETIC: {
    label: 'Energetic',
    icon: Zap,
    color: 'text-yellow-700 dark:text-yellow-400',
    chip: 'bg-yellow-500/10 border-yellow-500/20',
  },
  SOCIAL: {
    label: 'Social',
    icon: Users,
    color: 'text-pink-700 dark:text-pink-400',
    chip: 'bg-pink-500/10 border-pink-500/20',
  },
  GIGGLY: {
    label: 'Giggly',
    icon: Laugh,
    color: 'text-rose-700 dark:text-rose-400',
    chip: 'bg-rose-500/10 border-rose-500/20',
  },
  HUNGRY: {
    label: 'Hungry',
    icon: Cookie,
    color: 'text-emerald-700 dark:text-emerald-400',
    chip: 'bg-emerald-500/10 border-emerald-500/20',
  },
}

/** Enum order — matches the backend so pickers and lists stay stable. */
export const EFFECT_KEYS = Object.keys(EFFECT_VOCAB)

/**
 * Metadata for an effect. Unknown values (an enum member added server-side
 * before the client ships) degrade to a readable label with neutral styling
 * rather than crashing or rendering blank.
 */
export function effectMeta(value: string): EffectMeta {
  const known = EFFECT_VOCAB[value]
  if (known) return known
  return {
    label: value.charAt(0) + value.slice(1).toLowerCase(),
    icon: Sparkles,
    color: 'text-muted-foreground',
    chip: 'bg-muted border-border',
  }
}

export function effectLabel(value: string): string {
  return effectMeta(value).label
}
